import { actionProps } from "@/lib/controlAnalytics";
import { buttonClass } from "@/components/common/controls";
import { ArrowUpRightIcon, DownloadIcon, PinIcon } from "@/components/icons";
import type { Application } from "@/lib/api/envoy";
import { writtenResume } from "@/lib/hooks/envoy";
import { useOpenTailored } from "@/lib/hooks/resume";
import { pageTitle, usePageTitle } from "@/lib/pageTitle";
import { applicationChip } from "@/lib/posting";
import { postingPageName } from "@/lib/posting/page";
import { webUrl } from "@/lib/safeUrl";
import { SectionCard, StatusPill } from "./parts";

/** The address as a link only when it is an https page; anything else is shown as text. */
function httpsUrl(raw: string): string | undefined {
    const url = webUrl(raw);
    return url?.startsWith("https://") ? url : undefined;
}

/** What the card says, by where the application stands. */
function cardWords(
    application: Application,
    hasResume: boolean,
): { title: string; caption: string } {
    if (application.status === "FAILED")
        return {
            title: "Tailoring failed",
            caption: "The resume was not written. You can try again from the job's page.",
        };
    if (application.status === "TAILORING")
        return {
            title: "Tailoring in progress",
            caption: "Your resume is being written.",
        };
    return {
        title: hasResume
            ? "AI tailored on the company's own page"
            : "Tailored on the company's own page",
        caption: "This job is not in the catalogue. Your application is the record of it.",
    };
}

/**
 * A page posting's own page: a job tailored through the free resume check
 * or on a company's own site has no row in the catalogue, so the person's
 * application is all there is to show. The title, the company, where it
 * stands, a link to the job and the resume written for it.
 */
export default function OwnApplication({ application }: { application: Application }) {
    const openPdf = useOpenTailored();
    const chip = applicationChip(application);
    const resumeId = writtenResume(application);
    const link = httpsUrl(application.url);
    const words = cardWords(application, Boolean(resumeId));
    usePageTitle(pageTitle(postingPageName(application)));

    return (
        <div className="-mt-1 flex flex-col gap-5 text-ui">
            <div className="flex min-w-0 flex-col gap-2.5">
                <h1 className="m-0 font-display text-page-title font-bold tracking-[-0.025em] [text-wrap:balance]">
                    {application.title}
                </h1>
                <div className="flex flex-wrap items-center gap-2.5 text-ui">
                    <span className="text-subhead font-semibold">{application.company}</span>
                    {application.location && (
                        <span className="inline-flex items-center gap-1 text-fg-muted">
                            <PinIcon className="size-[13px] text-fg-faint" />
                            {application.location}
                        </span>
                    )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                    <StatusPill label={chip.label} tone={chip.tone} />
                </div>
            </div>

            <SectionCard title={words.title} caption={words.caption} className="gap-4">
                <div className="mt-4 flex flex-wrap gap-2">
                    {link && (
                        <a
                            {...actionProps("posting_source_open_clicked", {
                                source: "OwnApplication",
                                posting_id: application.posting_id,
                                option: "own_page",
                            })}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={buttonClass(
                                "secondary",
                                "page",
                                "gap-1.5 border-line-control",
                            )}
                        >
                            Open the job
                            <ArrowUpRightIcon className="size-3.5" />
                        </a>
                    )}
                    {resumeId && (
                        <button
                            {...actionProps("tailored_pdf_open_clicked", {
                                source: "OwnApplication",
                                resume_id: resumeId,
                                posting_id: application.posting_id,
                            })}
                            type="button"
                            onClick={() => openPdf.mutate(resumeId)}
                            className={buttonClass("primary", "page", "gap-1.5")}
                        >
                            <DownloadIcon className="size-3.5" strokeWidth={2.2} />
                            Open the resume
                        </button>
                    )}
                </div>
                {!link && application.url && (
                    <span className="break-all text-fg-muted">{application.url}</span>
                )}
            </SectionCard>
        </div>
    );
}
